import { ref } from 'vue';
import { CowsService } from '@shared/openapi/ganaderia-api';
import type { Cow, CowStatus, Selected, CowIds, UpdateCow, UpdateColVal } from '@shared/openapi/ganaderia-api';
import { db } from '@ganaderia/composables/useDexie';
import useSync from '@ganaderia/composables/useSync';
import useCowHelpers from '@ganaderia/composables/useCowHelpers';
import stores from '@ganaderia/stores';
import sharedStores from '@shared/shared-stores';

export default () => {
  const sync = useSync();
  const cowHelpers = useCowHelpers();
  const cows = ref<Cow[]>([]);

  const fetchAllCowsUpdateCache = async () => {
    if (!navigator.onLine) return;
    const res = await CowsService.readCowsV1CowsGet({ query: { status: 'all' } });
    if (res.data) {
      await db.cows.clear();
      await db.cows.bulkPut(res.data);
    }
  };

  const fetchCowIdsAndNumbers = async (): Promise<{ value: number | null; title: string }[]> => {
    const options: { value: number | null; title: string }[] = [{ value: null, title: 'Sin' }];
    if (navigator.onLine) {
      const res = await CowsService.getCowIdsAndNumbersV1CowsIdsNumbersGet();
      ((res.data as any) || []).forEach((c: any) => options.push({ value: c.id, title: c.number }));
    } else {
      const dbCows = await db.cows.toArray();
      dbCows.forEach((c: any) => options.push({ value: c.id, title: c.number }));
    }
    return options;
  };

  /** status: 'possession' | 'sold' | 'dead' */
  const fetchCows = async (status: 'possession' | 'sold' | 'dead') => {
    if (navigator.onLine) {
      const res = await CowsService.readCowsV1CowsGet({ query: { status: status as CowStatus } });
      const fetched = res.data ?? [];
      await db.cows.clear();
      if (fetched.length) await db.cows.bulkPut(fetched);
      cows.value = cowHelpers.addPregnantSinceAndAge(fetched as any) as any;
    } else {
      const dbCows = await db.cows.where({ status }).toArray();
      cows.value = cowHelpers.addPregnantSinceAndAge(dbCows as any) as any;
    }
    return cows.value;
  };

  const getCow = async (id: number) => {
    if (navigator.onLine) {
      const res = await CowsService.readCowV1CowsIdGet({ path: { id } });
      return res.data ?? null;
    }
    return (await db.cows.get(id)) ?? null;
  };

  const createCow = async (cow: Partial<Cow>) => {
    const res = await CowsService.createCowV1CowsPost({ body: cow as Cow });
    if (res.error) throw res;
    return res.data;
  };

  const updateCow = async (id: number, patch: UpdateCow) => {
    const res = await CowsService.updateCowV1CowsIdPatch({ path: { id }, body: patch });
    if (res.error) throw res;
    return res.data;
  };

  const updateCowColVal = async (id: number, patch: UpdateColVal) => {
    const res = await CowsService.updateCowColValV1CowsIdColValPatch({ path: { id }, body: patch });
    if (res.error) throw res;
  };

  const deleteCow = async (id: number) => {
    const res = await CowsService.deleteCowV1CowsIdDelete({ path: { id } });
    if (res.error) throw res;
  };

  const deleteCows = async (cowIds: CowIds) => {
    const res = await CowsService.deleteCowsV1CowsBulkDeletePost({ body: cowIds });
    if (res.error) throw res;
  };

  const deleteAbortion = async (id: number) => {
    const res = await CowsService.deleteAbortionV1CowsAbortionsIdDelete({ path: { id } });
    if (res.error) throw res;
  };

  const getBabies = async (motherId: number) => {
    const res = await CowsService.getBabiesV1CowsBabiesMotherIdGet({ path: { mother_id: motherId } });
    return res.data ?? [];
  };

  const addCsv = async (rows: any[]) => {
    const res = await CowsService.addCsvV1CowsAddCsvPost({ body: rows });
    if (res.error) throw res;
    return res.data;
  };

  /** Toggle a single cow's `selected` flag from the list view (online direct write, offline-queued). */
  const updateOneSelected = async (cow: Cow, selected: boolean) => {
    const url = `cows/${cow.id}`;
    await sync.queueOrSend({
      method: 'patch',
      url,
      payload: { selected },
      number: cow.number,
      farm: stores.app.state.selectedFarmId.value,
      action: 'Selection: Cambiar selected',
      message: `Selected: ${selected}`
    });
    if (!navigator.onLine) {
      await db.cows.update(cow.id!, { selected });
    }
  };

  /** Select/deselect every currently-filtered cow at once. */
  const updateAllSelected = async (selected: Selected, farmId: number) => {
    const url = `${farmId}/selected`;
    await sync.queueOrSend({
      method: 'post',
      url,
      payload: selected,
      farm: farmId,
      action: 'Revision: Cambiar revision',
      message: `Revisados: ${selected.selected.length}`
    });
  };

  const changeCowsFarm = async (cowIdsToMove: Cow[], newFarmId: number, fromFarmName: string) => {
    let successCount = 0;
    for (const cow of cowIdsToMove) {
      const observation =
        (cow.observation || '') + `${cow.observation ? '; ' : ''}` + `Viene de ${fromFarmName}`;
      const payload = { farm_id: newFarmId, observation };
      try {
        await sync.queueOrSend({
          method: 'patch',
          url: `cows/${cow.id}`,
          payload,
          number: cow.number,
          farm: newFarmId,
          action: 'Vaca: Actualizar',
          message: `Actualizar farm_id con ${newFarmId}`
        });
        if (!navigator.onLine) await db.cows.update(cow.id!, payload);
        successCount += 1;
      } catch {
        // counted via successCount below
      }
    }
    return successCount;
  };

  const bulkDeleteCows = async (cowsToDelete: Cow[]) => {
    const cowIds = cowsToDelete.map((c) => c.id!);
    if (navigator.onLine) {
      await deleteCows({ cow_ids: cowIds });
    } else {
      await db.cows.bulkDelete(cowIds);
      await db.syncs.add({
        method: 'post',
        url: 'cows-delete',
        payload: { cow_ids: cowIds },
        farm: stores.app.state.selectedFarmId.value,
        timestamp: new Date().toISOString(),
        action: 'Vaca: Eliminar multiples',
        message: `Eliminar vacas ${cowsToDelete.map((c) => c.number)}`
      });
      await sync.updateSyncLength();
    }
  };

  const notifyError = (error: any, prefix = 'Server error') => {
    sharedStores.snackbar.funcs.notify({ text: `${prefix}. ${error}` });
  };

  return {
    state: { cows },
    funcs: {
      fetchAllCowsUpdateCache,
      fetchCowIdsAndNumbers,
      fetchCows,
      getCow,
      createCow,
      updateCow,
      updateCowColVal,
      deleteCow,
      deleteCows,
      deleteAbortion,
      getBabies,
      addCsv,
      updateOneSelected,
      updateAllSelected,
      changeCowsFarm,
      bulkDeleteCows,
      notifyError
    }
  };
};
